import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronRight, Info, AlertTriangle, Sun, Calendar, CheckCircle, XCircle } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';

/**
 * Infobox für Sonntagsarbeits-Regeln nach § 11 ArbZG
 * 
 * Zeigt die dynamisch generierten Regeln an.
 * Nur für Admin/Supervisor/HR sichtbar.
 */
const SundayRulesInfoBox = ({ compact = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});

  useEffect(() => {
    const fetchRules = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/compliance/sunday-rules-explanation`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        if (response.status === 403) {
          // Keine Berechtigung - still ignorieren
          setData(null);
          return;
        }
        
        if (!response.ok) {
          throw new Error('Fehler beim Laden');
        }
        
        const result = await response.json();
        setData(result);
        setError(null);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchRules();
  }, []);

  const toggleSection = (index) => {
    setExpandedSections(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground text-sm p-4">
        <div className="w-4 h-4 border-2 border-border border-t-zinc-400 rounded-full animate-spin" />
        <span>Lade Sonntagsregeln...</span>
      </div>
    );
  }

  if (error || !data) {
    return null;
  }

  if (compact) {
    return (
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger className="flex items-center gap-2 text-sm text-muted-foreground hover:text-muted-foreground transition-colors w-full">
          {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          <Sun className="w-4 h-4 text-amber-600 dark:text-amber-400" />
          <span>Sonntagsarbeits-Regeln (§ 11 ArbZG)</span>
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-3">
          <div className="p-4 bg-muted/50 rounded-lg border border-border/50 space-y-3">
            {data.sections?.map((section, index) => (
              <div key={index}>
                <h4 className="text-sm font-medium text-muted-foreground">{section.title}</h4>
                <p className="text-xs text-muted-foreground mt-1">{section.text}</p>
              </div>
            ))}
            <div className="pt-2 border-t border-border/50">
              <p className="text-xs text-muted-foreground italic">{data.disclaimer}</p>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
    );
  }

  return (
    <div className="card-glass p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-heading font-semibold text-foreground flex items-center gap-2">
          <Sun className="w-5 h-5 text-amber-600 dark:text-amber-400" />
          Sonntagsarbeit & Ersatzruhetag (§ 11 ArbZG)
        </h3>
      </div>

      <div className="space-y-2">
        {data.sections?.map((section, index) => (
          <Collapsible 
            key={index} 
            open={expandedSections[index]} 
            onOpenChange={() => toggleSection(index)}
          >
            <CollapsibleTrigger className="flex items-center gap-2 w-full p-3 bg-muted/30 hover:bg-muted/50 rounded-lg transition-colors text-left">
              {expandedSections[index] ? (
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              )}
              <span className="font-medium text-foreground">{section.title}</span>
            </CollapsibleTrigger>
            <CollapsibleContent className="px-3 pb-3">
              <p className="text-sm text-muted-foreground mt-2 pl-6">{section.text}</p>
            </CollapsibleContent>
          </Collapsible>
        ))}
      </div>

      <div className="pt-4 border-t border-border/50">
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <span>Frist Ersatzruhetag: {data.settings?.replacement_deadline_days} Tage</span>
          <span>Min. freie Sonntage/Jahr: {data.settings?.min_free_sundays_per_year}</span>
        </div>
      </div>

      <div className="pt-2 border-t border-border/50">
        <p className="text-xs text-muted-foreground italic flex items-start gap-2">
          <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
          {data.disclaimer}
        </p>
      </div>
    </div>
  );
};

/**
 * Hinweis bei Sonntagsarbeit-Erfassung
 */
export const SundayWorkHint = ({ sundayWorkHint }) => {
  if (!sundayWorkHint) return null;

  return (
    <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg mt-2">
      <div className="flex items-start gap-2">
        <Sun className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5" />
        <div className="flex-1">
          <div className="text-sm text-amber-600 dark:text-amber-400">{sundayWorkHint.message}</div>
          <div className="text-xs text-muted-foreground mt-1">
            Frist bis: {sundayWorkHint.deadline}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Kompakte Anzeige des Sonntagsarbeit-Status in der Wochenansicht
 */
export const SundayWorkIndicator = ({ date, compliance }) => {
  if (!compliance) return null;

  const sundayEntry = compliance.sunday_work_days?.find(s => s.date === date);
  if (!sundayEntry) return null;

  const isCompliant = sundayEntry.compliant;

  return (
    <div className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs ${
      isCompliant 
        ? 'bg-emerald-500/20 text-emerald-400' 
        : 'bg-red-500/20 text-red-400'
    }`} title={isCompliant 
      ? `Ersatzruhetag: ${sundayEntry.replacement_day}` 
      : `Ersatzruhetag fehlt (Frist: ${sundayEntry.deadline})`
    }>
      <Sun className="w-3 h-3" />
      {isCompliant ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
    </div>
  );
};

/**
 * Team-Übersicht für offene Ersatzruhetag-Verstöße
 */
export const SundayComplianceTeamList = ({ dateFrom, dateTo }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTeamData = async () => {
      if (!dateFrom || !dateTo) return;
      
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
        
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/compliance/sunday-replacement/team?${params}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        if (response.status === 403) {
          setError('Keine Berechtigung');
          return;
        }
        
        if (!response.ok) {
          throw new Error('Fehler beim Laden');
        }
        
        const result = await response.json();
        setData(result);
        setError(null);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchTeamData();
  }, [dateFrom, dateTo]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4 text-muted-foreground">
        <div className="w-5 h-5 border-2 border-border border-t-zinc-400 rounded-full animate-spin mr-2" />
        Lade Team-Daten...
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-muted-foreground text-center text-sm">
        {error}
      </div>
    );
  }

  if (!data || data.employees.length === 0) {
    return (
      <div className="p-4 text-center">
        <CheckCircle className="w-8 h-8 text-emerald-600 dark:text-emerald-400 mx-auto mb-2" />
        <div className="text-muted-foreground">Keine Sonntagsarbeit im Zeitraum</div>
        <div className="text-xs text-muted-foreground mt-1">
          oder alle Ersatzruhetage wurden gewährt
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-sm text-muted-foreground px-1">
        <span className="flex items-center gap-1">
          <Sun className="w-4 h-4 text-amber-600 dark:text-amber-400" />
          {data.total_employees_with_sunday_work} Mitarbeiter mit Sonntagsarbeit
        </span>
        {data.total_open_violations > 0 && (
          <span className="text-red-600 dark:text-red-400 flex items-center gap-1">
            <AlertTriangle className="w-4 h-4" />
            {data.total_open_violations} offene Verstöße
          </span>
        )}
      </div>
      
      <div className="space-y-1">
        {data.employees.map((emp) => (
          <div
            key={emp.user_id}
            className={`flex items-center justify-between p-2 rounded-lg border ${
              emp.is_compliant 
                ? 'bg-emerald-500/5 border-emerald-500/20' 
                : 'bg-red-500/10 border-red-500/20'
            }`}
          >
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${emp.is_compliant ? 'bg-emerald-400' : 'bg-red-400'}`} />
              <span className="text-foreground text-sm">{emp.user_name}</span>
            </div>
            <div className="text-right">
              <div className="text-sm text-muted-foreground">
                {emp.sunday_work_days} Sonntag{emp.sunday_work_days !== 1 ? 'e' : ''} gearbeitet
              </div>
              {emp.open_violations > 0 && (
                <div className="text-xs text-red-600 dark:text-red-400">
                  {emp.open_violations} ohne Ersatzruhetag
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Anzeige der freien Sonntage im Jahr
 */
export const FreeSundaysCounter = ({ year }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/compliance/free-sundays/${year}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        if (response.ok) {
          const result = await response.json();
          setData(result);
        }
      } catch (err) {
        console.error('Error fetching free sundays:', err);
      } finally {
        setLoading(false);
      }
    };

    if (year) fetchData();
  }, [year]);

  if (loading || !data) return null;

  return (
    <div className={`p-3 rounded-lg border ${
      data.is_compliant 
        ? 'bg-emerald-500/10 border-emerald-500/20' 
        : 'bg-red-500/10 border-red-500/20'
    }`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sun className={`w-4 h-4 ${data.is_compliant ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`} />
          <span className="text-sm text-muted-foreground">Freie Sonntage {year}</span>
        </div>
        <span className={`text-lg font-bold ${data.is_compliant ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
          {data.free_sundays} / {data.min_required_free}
        </span>
      </div>
      {!data.is_compliant && (
        <p className="text-xs text-red-600 dark:text-red-400 mt-1">
          Noch {data.sundays_needed_for_compliance} freie Sonntage benötigt (§ 11 Abs. 1 ArbZG)
        </p>
      )}
    </div>
  );
};

export default SundayRulesInfoBox;
